import { EnvironmentRef } from './../../../environments/interfaces/environment.interface';
import { expEnvironmentRef } from './../../injection-tokens/environment.injection-token';
import { LoggerService } from './../logger/logger.service';
import { ScriptInjectorService } from './../script-injector/script-injector.service';
import { Inject, Injectable } from '@angular/core';
import { windowInjectionToken } from '../../injection-tokens/window.injection-token';
import { get as lodashGet, hasIn as lodashHasIn } from 'lodash';
import { AnalyticsDataLayer  } from './definitions';
import { AnalyticsFactory } from './analytics.factory';
import { SharedService } from '../shared/shared.service';
import { SharedUtilService } from '../shared/shared-util.service';
@Injectable({
	providedIn: 'root'
})
export class AnalyticsService
{

	private readonly adobeDataLayerName = 'dl';

	constructor(
		@Inject(windowInjectionToken) private readonly windowRef: Window,
		@Inject(expEnvironmentRef) private readonly environmentRef: EnvironmentRef,
		private readonly logger: LoggerService,
		private readonly scriptInjectorService: ScriptInjectorService,
		private readonly sharedService: SharedService,
		private readonly sharedUtilService: SharedUtilService
	){}

	public initDataLayer(dl: AnalyticsDataLayer = AnalyticsFactory.buildDataLayerInstance()): void
	{
		this.pushData(dl);
		this.logger.log('Initializing Analytics Data Layer', dl);
	}
	public async injectAdobeLaunchScript(): Promise<void>
	{
		try
		{
			await this.scriptInjectorService.load(
				'launch',
				this.environmentRef.analytics.scripts.launch.src
			);

			(lodashGet(this.windowRef, '_satellite') as unknown as {pageBottom: () => void})
				.pageBottom();
		}
		catch (error)
		{
			this.logger.error('Error loading Adobe launch script', error);
		}
	}
	async trackByEvent(data: AnalyticsDataLayer)
	{
		await this.sharedUtilService.isLoggedInUserPresent();
		this.pushData({
			...data,
			user: {
				email: this.sharedService.getEmailAddress()
			},
			site: {
				viewport: (lodashGet(this.windowRef, 'visualViewport.width', 0)) > 768 ? 'desktop' : 'mobile',
				language: (this.windowRef.document.documentElement.getAttribute('lang') || '').includes('fr') ? 'fr' : 'en'
			}
		});
	}
	public trackErrorByEvent(errorMessage: string): void
	{
		this.trackByEvent({
			event: 'error',
			link: {
				name: 'error'
			},
			error: {
				message: errorMessage
			}
		});
	}
	public trackRegistrationByEvent(data: AnalyticsDataLayer, email: string)
	{
		this.pushData({
			...data,
			user: {
				email: email
			},
			site: {
				viewport: lodashGet(this.windowRef, 'visualViewport.width', 0) > 768 ? 'desktop' : 'mobile',
				language: (this.windowRef.document.documentElement.getAttribute('lang') || '').includes('fr') ? 'fr' : 'en'
			}
		});
	}
	private pushData(data: AnalyticsDataLayer): void
	{
		try
		{
			const dataLayerIsInit: boolean = lodashHasIn(this.windowRef, `${this.adobeDataLayerName}`);
			if (!dataLayerIsInit)
			{
				throw new Error('Data layer not initiated');
			}
			if (data.event === null)
			{
				this.logger.warn('No event provided in data layer. It wont be registered against adobe launch', data);
			}
			if(data.event !== null)
			{
				const adobeAnalyticsDataLayer: {
          [key: string]: string | ((value: { [key: string]: string }) => void);
          push: (value: { [key: string]: string }) => void;
        }  = lodashGet(this.windowRef,  this.adobeDataLayerName);
				adobeAnalyticsDataLayer.push(data as { [key: string]: string } );
				this.logger.log('Analytics object pushed', data);
			}

		}
		catch (error)
		{
			this.logger.error('Unable to push analytics data', error);
		}
	}
}
